import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/** One code and quantity a scheme actually authorised. */
export interface AuthorisedCode {
  code: string;
  qty: number;
}

/**
 * A medical-scheme authorisation as a REAL ROW — `PatientAuthorizations`.
 *
 * The thing this has that the JSON blob on `Patient.authorizationNumbers` does not is an `id`
 * that survives a save. The blob's id is a client-side array index generated at
 * `auth-list-creator.component.ts:26` and stripped before emit (`:64-70`), so nothing can
 * reference one — which is why `appointmentId` below is impossible in the old shape and is the
 * whole point of the new one.
 */
export interface PatientAuthorization {
  id: string;
  patientId: string;
  groupId: string;
  authNumber: string;
  /** The keyed blind index. The ONLY way to compare two authorisation numbers server-side. */
  hashedAuthNumber?: string;
  schemeName: string | null;
  schemeNumber: string | null;
  authorisedFrom: string | null;
  authorisedTo: string | null;
  authorisedDays: number | null;
  authorisedCodes: AuthorisedCode[];
  appointmentId: string | null;
  admissionId: string | null;
  status: 'requested' | 'approved' | 'declined' | 'expired';
  notes: string | null;
  active: boolean;
  source?: 'table';
  createdAt?: string;
  updatedAt?: string;
}

/**
 * An authorisation that still lives ONLY in one of the two encrypted JSON blobs.
 *
 * `scripts/backfill-patient-authorizations.js` exists and has NOT been run — 12,903 rows is the
 * repository owner's decision. Until it does, this is where most practices' authorisations are,
 * so the list shows them: read-only, marked, and impossible to scope to an appointment or give
 * codes to, because the blob has nowhere to put either.
 *
 * The server drops any blob entry whose number already exists in the table (compared by blind
 * index), so after the backfill runs this array empties itself. Nothing needs changing here.
 */
export interface LegacyAuthorization {
  authNumber: string;
  date: string | null;
  comments: string | null;
  origin: 'patient' | 'admission';
  originId: string;
  hashedAuthNumber: string | null;
  source: 'legacy-blob';
}

export interface AuthorizationListResponse {
  success: boolean;
  message?: string;
  data: PatientAuthorization[];
  legacy: LegacyAuthorization[];
}

/** B4. Every field is advisory. `blocked` is hard-coded false on the server. */
export interface CoverageWarning {
  type: 'no-authorisation' | 'authorisation-has-no-codes' | 'code-not-authorised'
  | 'quantity-exceeds-authorisation' | 'authorisation-expired'
  | 'authorisation-not-yet-valid' | 'authorisation-declined';
  severity: 'warning';
  message: string;
  code?: string;
  description?: string | null;
  source?: string;
  quantity?: number;
  authorisedQuantity?: number;
  authorizationId?: string | null;
  authNumber?: string | null;
  authorisedFrom?: string;
  authorisedTo?: string;
  dateOfService?: string;
}

export interface CoverageResponse {
  success: boolean;
  /** Always false. Nothing in B4 stops a save — §7, "warn, never block". */
  blocked: boolean;
  message?: string;
  dateOfService?: string | null;
  warnings: CoverageWarning[];
  authorizations?: PatientAuthorization[];
  codes?: Array<{ code: string; quantity: number; description?: string | null; source?: string }>;
}

export interface AuthorizationWriteResponse {
  success: boolean;
  message?: string;
  data: PatientAuthorization;
  coverage?: CoverageResponse;
}

/**
 * Client for `controllers/patientAuthorizationController.js` — tasks B3 and B4.
 *
 * Nothing in this service writes the legacy blob. That column is still edited by
 * `auth-list-creator.component.ts` and still saved by the patient form, deliberately: the
 * backfill has not run, so removing that path would strand every practice's existing
 * authorisations.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientAuthorizationService {
  private apiUrl = 'api/patient-authorizations';

  constructor(private http: HttpClient) { }

  private authHeaders() {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }

  /** Table rows in `data`, un-migrated blob entries in `legacy`. */
  loadByPatient(patientId: string, includeInactive = false): Observable<AuthorizationListResponse> {
    const query = includeInactive ? '?includeInactive=true' : '';
    return this.http.get<AuthorizationListResponse>(`${this.apiUrl}/patient/${patientId}${query}`, {
      headers: this.authHeaders()
    });
  }

  /** What one booking is authorised for — the Netcare form's per-case auth number reads this. */
  loadByAppointment(appointmentId: string): Observable<{ success: boolean; data: PatientAuthorization[] }> {
    return this.http.get<{ success: boolean; data: PatientAuthorization[] }>(
      `${this.apiUrl}/appointment/${appointmentId}`, { headers: this.authHeaders() });
  }

  create(authorization: Partial<PatientAuthorization> & { patientId: string; authNumber: string })
    : Observable<AuthorizationWriteResponse> {
    return this.http.post<AuthorizationWriteResponse>(this.apiUrl, { authorization }, {
      headers: this.authHeaders()
    });
  }

  update(id: string, authorization: Partial<PatientAuthorization>): Observable<AuthorizationWriteResponse> {
    return this.http.put<AuthorizationWriteResponse>(`${this.apiUrl}/${id}`, { authorization }, {
      headers: this.authHeaders()
    });
  }

  /** Scope to a booking, or pass null to unscope. Its own endpoint — see the controller header. */
  scopeToAppointment(id: string, appointmentId: string | null): Observable<AuthorizationWriteResponse> {
    return this.http.post<AuthorizationWriteResponse>(`${this.apiUrl}/${id}/appointment`,
      { appointmentId }, { headers: this.authHeaders() });
  }

  /** Soft delete: `active = false`. An authorisation is evidence, so the row stays. */
  deactivate(id: string): Observable<AuthorizationWriteResponse> {
    return this.http.delete<AuthorizationWriteResponse>(`${this.apiUrl}/${id}`, {
      headers: this.authHeaders()
    });
  }

  /**
   * B4. Omit `codes` and the server compares the patient's PLANNED codes; pass them to check
   * what is about to be billed. Never blocks — a warning is the entire output.
   */
  checkCoverage(payload: {
    patientId: string;
    appointmentId?: string | null;
    dateOfService?: string | null;
    codes?: Array<{ code: string; quantity?: number; description?: string | null; source?: string; isModifier?: boolean }>;
  }): Observable<CoverageResponse> {
    return this.http.post<CoverageResponse>(`${this.apiUrl}/coverage`, payload, {
      headers: this.authHeaders()
    });
  }
}
